import { useEffect, useMemo, useState, type ReactNode } from "react";
import type { AttachmentInfo, CalAttendee, CalEvent, Self } from "../types";
import { agendaItemCount, LIST_ITEM_RE, type InviteContent } from "../domain/agenda";
import { cancelChange, moveModeFor, respondChange, type CalendarChange } from "../domain/changes";
import { displaySubject, isActive, isFocus, isUnsentDraft, needsReply } from "../domain/day";
import { busyDuring, type Schedule } from "../domain/freeBusy";
import { peopleToCheck } from "../domain/resolve";
import { dayKeyOf, dayLabel, fd, hm, minutesOfDay } from "../domain/time";
import { useAvailability, type LoadSchedules } from "../hooks/useAvailability";
import { useCalendarKeys } from "../hooks/useCalendarKeys";
import { useEventDetail } from "../hooks/useEventDetail";
import { useMeetingThreads } from "../hooks/useMeetingThreads";
import { usePrepBrief } from "../hooks/usePrepBrief";
import type { MeetingThread } from "../services/meetingThread";
import { openInOutlook, openJoinLink } from "../services/outlook";
import {
  CameraIcon,
  ChevronLeftSmIcon,
  ChevronRightSmIcon,
  ClockIcon,
  PinIcon,
  RepeatIcon,
  SparkIcon,
  UserIcon,
  WarnIcon,
} from "./CalendarIcons";
import { badgesFor } from "./eventBadges";
import { MeetingAgenda, previewText, savedAgenda } from "./MeetingAgenda";
import { MeetingAttachments } from "./MeetingAttachments";
import { MeetingAttendees } from "./MeetingAttendees";
import styles from "./CalendarModule.module.css";

const REPLIES = [
  { value: "yes", label: "Accept", on: styles.rsvpYes! },
  { value: "may", label: "Tentative", on: styles.rsvpMay! },
  { value: "no", label: "Decline", on: styles.rsvpNo! },
] as const;

/** An agenda item takes at least this long; more items than fit the meeting at this pace is called tight. */
const MIN_ITEM_MIN = 5;

interface MeetingDetailProps {
  readonly event: CalEvent;
  /** The meeting's day, in order, for the "2 of 5" pager and the brief. */
  readonly dayEvents: readonly CalEvent[];
  /** The other meeting this one overlaps, if any. */
  readonly clashWith: CalEvent | null;
  readonly token: string | null;
  readonly self: Self;
  readonly now: Date;
  /** Whether the token allows changing the calendar from AiVA. */
  readonly writable: boolean;
  readonly loadSchedules: LoadSchedules;
  readonly onStage: (change: CalendarChange) => void;
  readonly onOpenEvent: (eventId: string) => void;
  readonly onSeeOptions: () => void;
  readonly onReschedule: () => void;
  /** An unsent draft was opened in Outlook, to send it there: the calendar is read again on return. */
  readonly onOpenedInOutlook: () => void;
  /** The meeting's latest email thread, for Linked Material. */
  readonly loadThread: (event: CalEvent) => Promise<MeetingThread | null>;
}

const firstName = (name: string) => name.trim().split(/\s+/)[0] ?? name;
const hasAttendees = (e: CalEvent) => e.attendees.some((a) => a.type !== "resource");

/** "Mon 3 Aug · 11:30–12:30 · 1h", or "Mon 3 Aug · All day". */
function whenLine(e: CalEvent): string {
  const day = dayLabel(dayKeyOf(e.start));
  if (e.isAllDay) return `${day} · All day`;
  const minutes = Math.round((e.end.getTime() - e.start.getTime()) / 60000);
  return `${day} · ${hm(minutesOfDay(e.start))}–${hm(minutesOfDay(e.end))} · ${fd(minutes)}`;
}

/** The meeting's other people: everyone except you (under any of your addresses), the organiser and rooms; required first. */
function otherAttendees(e: CalEvent, selfEmails: ReadonlySet<string>): CalAttendee[] {
  return e.attendees
    .filter((a) => a.type !== "resource" && !selfEmails.has(a.email) && a.email !== e.organizerEmail)
    .sort((a, b) => requiredFirst(a) - requiredFirst(b));
}

const requiredFirst = (a: CalAttendee) => (a.type === "required" ? 0 : 1);

/** How many items the agenda has: the agenda block's items, else the list lines in the invite's own text. */
function agendaItems(e: CalEvent, invite: InviteContent | null): number {
  const saved = savedAgenda(e, invite);
  if (saved) return agendaItemCount(saved);
  return (invite?.text ?? previewText(e)).split("\n").filter((line) => LIST_ITEM_RE.test(line)).length;
}

interface BriefInput {
  readonly others: readonly CalAttendee[];
  readonly attachments: readonly AttachmentInfo[];
  readonly dayEvents: readonly CalEvent[];
  /** People known to have something else on at this time. */
  readonly busyNames: readonly string[];
  readonly agendaItems: number;
}

/** The brief's first line: who has replied to your meeting, or who organised one you're invited to. */
function peopleLine(e: CalEvent, others: readonly CalAttendee[]): { lead: string; text: string } {
  if (!e.isOrganizer) return { lead: `${others.length + 2} people invited.`, text: `Organised by ${e.organizerName}` };
  const names = (r: CalAttendee["response"]) => others.filter((a) => a.response === r).map((a) => firstName(a.name));
  const confirmed = names("yes");
  const parts = [confirmed.length ? `${confirmed.join(", ")} confirmed` : "No one confirmed yet"];
  if (names("may").length) parts.push(`${names("may").join(", ")} tentative`);
  if (names("wait").length) parts.push(`${names("wait").join(", ")} not replied`);
  return { lead: `${others.length + 1} people.`, text: parts.join("; ") };
}

/** Prep-brief lines, computed from the meeting itself (the mockup's buildBrief rules). */
function briefLines(e: CalEvent, input: BriefInput): { lead: string; text: string }[] {
  const { others, attachments, dayEvents, busyNames } = input;
  const lines: { lead: string; text: string }[] = [peopleLine(e, others)];
  if (busyNames.length) {
    lines.push({
      lead: "Heads-up:",
      text: `${busyNames.join(", ")} ${busyNames.length > 1 ? "have" : "has"} another commitment at this time`,
    });
  }
  if (attachments.length) lines.push({ lead: "Read first:", text: attachments.map((a) => a.name).join(", ") });
  const minutes = Math.round((e.end.getTime() - e.start.getTime()) / 60000);
  if (input.agendaItems > 0) {
    const n = input.agendaItems;
    const each = Math.floor(minutes / n);
    const items = n > 1 ? "items" : "item";
    // More items than the meeting has time for at a sensible pace is said plainly, not rounded up.
    const text = each >= MIN_ITEM_MIN ? `${n} ${items} — roughly ${fd(each)} each` : `${n} items for ${fd(minutes)} — that's tight`;
    lines.push({ lead: "Agenda:", text });
  } else if (e.isOrganizer) {
    lines.push({ lead: "No agenda yet.", text: "Draft one so attendees can prepare" });
  } else {
    lines.push({ lead: "No agenda", text: "in the invite" });
  }
  const before = dayEvents
    .filter((o) => o.id !== e.id && !o.isAllDay && isActive(o) && o.end.getTime() <= e.start.getTime())
    .sort((a, b) => a.end.getTime() - b.end.getTime())
    .pop();
  if (before) {
    const gap = Math.round((e.start.getTime() - before.end.getTime()) / 60000);
    const prepare = gap < 10 ? "no gap to prepare" : `${fd(gap)} to prepare`;
    lines.push({ lead: "Before this:", text: `${displaySubject(before)} ends ${hm(minutesOfDay(before.end))} — ${prepare}` });
  }
  return lines;
}

/** The first names of `people` known to have something else on during `event`; none while availability loads. */
/** The repeat pattern: a shimmer while it loads, then the pattern (faded in), or a fallback if it's unknown. */
function RepeatLine({ recurrence }: Readonly<{ recurrence: string | null | undefined }>) {
  return (
    <div className={styles.dLine}>
      <RepeatIcon />
      {recurrence === undefined ? (
        <span className={`${styles.skel} ${styles.skelInline}`} aria-hidden="true" />
      ) : (
        <span className={styles.fadeIn}>{recurrence ?? "Part of a recurring series"}</span>
      )}
    </div>
  );
}

function busyFirstNames(event: CalEvent, people: readonly string[], availability: ReadonlyMap<string, Schedule> | null): string[] {
  if (!availability) return [];
  return people
    .filter((email) => busyDuring(availability, email, event, true) === true)
    .map((email) =>
      firstName(email === event.organizerEmail ? event.organizerName : (event.attendees.find((a) => a.email === email)?.name ?? email)),
    );
}

interface ReplySectionProps {
  readonly event: CalEvent;
  readonly writable: boolean;
  readonly onStage: (change: CalendarChange) => void;
}

/** Your Reply to a meeting someone else organised: Accept, Tentative or Decline. */
function ReplySection({ event, writable, onStage }: Readonly<ReplySectionProps>) {
  const reply = needsReply(event);
  return (
    <div className={styles.sec}>
      <div className={styles.secH}>
        <span className={`${styles.secBar} ${reply ? styles.secBarNotice : ""}`} />
        <span className={`${styles.secTitle} ${reply ? styles.secTitleNotice : ""}`}>Your Reply</span>
        {event.isRecurring && writable && <span className={`${styles.secRight} ${styles.secMeta}`}>This occurrence</span>}
      </div>
      {writable ? (
        <fieldset className={styles.rsvp} aria-label="Your reply">
          {REPLIES.map((r) => (
            <button
              key={r.value}
              type="button"
              className={event.response === r.value ? r.on : ""}
              aria-pressed={event.response === r.value}
              onClick={() => event.response !== r.value && onStage(respondChange(event, r.value))}
            >
              {r.label}
            </button>
          ))}
        </fieldset>
      ) : (
        <div className={styles.hint}>Reply from Outlook — AiVA can't change your calendar with your current permissions.</div>
      )}
    </div>
  );
}

interface MeetingActionsProps {
  readonly event: CalEvent;
  readonly writable: boolean;
  readonly moveMode: ReturnType<typeof moveModeFor>;
  readonly canCancel: boolean;
  readonly onReschedule: () => void;
  readonly onStage: (change: CalendarChange) => void;
}

/** Reschedule (or Propose Time) and Cancel (or Remove), where the meeting allows them. */
function MeetingActions({ event, writable, moveMode, canCancel, onReschedule, onStage }: Readonly<MeetingActionsProps>) {
  const shown = writable && (moveMode !== "none" || canCancel);
  if (!shown) return null;
  return (
    <div className={styles.rowBtns}>
      {moveMode !== "none" && (
        <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={onReschedule}>
          {moveMode === "reschedule" ? "Reschedule" : "Propose Time"}
        </button>
      )}
      {canCancel && (
        <button
          type="button"
          className={`${styles.btn} ${styles.btnSecondary} ${styles.btnDanger}`}
          onClick={() => onStage(cancelChange(event))}
        >
          {hasAttendees(event) ? "Cancel" : "Remove"}
        </button>
      )}
    </div>
  );
}

/** One meeting in full: when and where, your reply, who, the agenda, attachments and a prep brief. */
export function MeetingDetail({
  event,
  dayEvents,
  clashWith,
  token,
  self,
  now,
  writable,
  loadSchedules,
  onStage,
  onOpenEvent,
  onSeeOptions,
  onReschedule,
  onOpenedInOutlook,
  loadThread,
}: Readonly<MeetingDetailProps>) {
  const { attachments, recurrence, invite, failed: inviteFailed } = useEventDetail(token, event);
  const thread = useMeetingThreads(loadThread, [event]).get(event.id);
  // Prepare for This Meeting, as in the mockup: opened per meeting, "Preparing" while AiVA writes it, then the
  // brief with Refresh. Paging to another meeting shows that meeting's button again.
  const [briefFor, setBriefFor] = useState<string | null>(null);
  const briefOpen = briefFor === event.id;
  const aiBrief = usePrepBrief();

  const index = dayEvents.findIndex((e) => e.id === event.id);
  const prev = index > 0 ? dayEvents[index - 1] : undefined;
  const next = index >= 0 ? dayEvents[index + 1] : undefined;
  const day = dayKeyOf(event.start);
  const badges = badgesFor(event, clashWith ? new Set([event.id]) : new Set());
  const others = otherAttendees(event, self.emails);
  const focus = isFocus(event);
  const local = event.id.startsWith("local-");
  const moveMode = moveModeFor(event);
  const canCancel = event.isOrganizer && !event.isCancelled && !local;
  const canEdit = writable && event.isOrganizer && !event.isCancelled && !local;

  // ← and → step through the day's meetings, as the pager buttons do (the mockup's keys).
  useCalendarKeys((e) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
    const to = e.key === "ArrowLeft" ? prev?.id : next?.id;
    if (!to) return;
    e.preventDefault();
    onOpenEvent(to);
  });

  // Colleagues' free/busy at this time, for "Busy at this time" and the brief's heads-up. Not worth reading once it's over.
  const people = useMemo(() => peopleToCheck(event, self.emails), [event, self.emails]);
  const upcoming = event.end.getTime() > now.getTime();
  const availability = useAvailability(loadSchedules, people, event.start, event.end, upcoming && !event.isCancelled && !focus);
  const busyNames = busyFirstNames(event, people, availability);

  // The brief's facts, worked out here; AiVA rewrites the text after each lead with what the email thread adds.
  const briefReady =
    !(event.hasAttachments && attachments === null) && !(invite === null && !inviteFailed) && availability !== null;
  const facts = briefLines(event, {
    others,
    attachments: attachments ?? [],
    dayEvents,
    busyNames,
    agendaItems: agendaItems(event, invite),
  });
  const writeBrief = () =>
    void aiBrief.start({
      eventId: event.id,
      subject: displaySubject(event),
      organizerEmail: event.organizerEmail,
      attendeeEmails: event.attendees.filter((a) => a.type !== "resource").map((a) => a.email),
      inviteText: invite?.text ?? previewText(event),
      facts,
      userDisplayName: self.name,
    });
  // Once the brief is open and its facts are ready, AiVA writes it: once per opening, and again on Refresh.
  const briefStarted = aiBrief.state.status !== "idle" && aiBrief.state.eventId === event.id;
  useEffect(() => {
    if (briefOpen && briefReady && !briefStarted) writeBrief();
  }, [briefOpen, briefReady, briefStarted]);
  const briefShown =
    aiBrief.state.status === "done" && aiBrief.state.eventId === event.id ? aiBrief.state.lines : facts;
  const briefFailure =
    aiBrief.state.status === "failed" && aiBrief.state.eventId === event.id ? aiBrief.state.message : null;
  const briefWriting = !briefReady || !briefStarted || aiBrief.state.status === "writing";

  // Prepare for This Meeting: the button, then "Preparing" while AiVA writes, then the brief. None for focus time.
  const brief = (): ReactNode => {
    if (focus) return null;
    if (!briefOpen) {
      return (
        <button type="button" className={`${styles.btn} ${styles.btnIcon}`} onClick={() => setBriefFor(event.id)}>
          <SparkIcon />
          Prepare for This Meeting
        </button>
      );
    }
    if (briefWriting) {
      // The mockup's "Preparing", for as long as AiVA is writing (or the facts are still loading).
      return (
        <div className={`${styles.band} ${styles.bandAi}`} aria-busy="true">
          <div className={styles.bandTitle}>
            <SparkIcon />
            Preparing
          </div>
          <div className={styles.skelStack}>
            <div className={styles.skel} style={{ width: "90%" }} />
            <div className={styles.skel} style={{ width: "75%" }} />
            <div className={styles.skel} style={{ width: "60%" }} />
          </div>
        </div>
      );
    }
    return (
      <div className={`${styles.band} ${styles.bandAi}`}>
        <div className={styles.bandTitle}>
          <SparkIcon />
          Prep Brief
          <button type="button" className={`${styles.newLink} ${styles.bandTitleLink}`} onClick={writeBrief}>
            Refresh
          </button>
        </div>
        <ul className={styles.brief}>
          {briefShown.map((line) => (
            <li key={line.lead}>
              <b>{line.lead}</b> {line.text}
            </li>
          ))}
        </ul>
        {briefFailure && (
          <span className={`${styles.hint} ${styles.bandHint}`}>{briefFailure} These are the facts from your calendar.</span>
        )}
      </div>
    );
  };

  return (
    <>
      <div className={styles.pager}>
        <button
          type="button"
          className={`${styles.icoBtn} ${styles.icoBtnSm}`}
          onClick={() => prev && onOpenEvent(prev.id)}
          disabled={!prev}
          aria-label="Previous meeting"
          aria-keyshortcuts="ArrowLeft"
          title="Previous meeting (←)"
        >
          <ChevronLeftSmIcon />
        </button>
        <span>
          {index + 1} of {dayEvents.length} on {dayLabel(day)}
        </span>
        <button
          type="button"
          className={`${styles.icoBtn} ${styles.icoBtnSm}`}
          onClick={() => next && onOpenEvent(next.id)}
          disabled={!next}
          aria-label="Next meeting"
          aria-keyshortcuts="ArrowRight"
          title="Next meeting (→)"
        >
          <ChevronRightSmIcon />
        </button>
      </div>

      <div className={styles.dSummary}>
        <div className={styles.dTitle}>{displaySubject(event)}</div>
        <div className={styles.dLine}>
          <ClockIcon />
          {whenLine(event)}
        </div>
        {event.location && (
          <div className={styles.dLine}>
            <PinIcon />
            {event.location}
          </div>
        )}
        <div className={styles.dLine}>
          <UserIcon />
          Organiser: {event.isOrganizer ? "You" : event.organizerName}
        </div>
        {event.isRecurring && <RepeatLine recurrence={recurrence} />}
        {badges.length > 0 && <div className={styles.dBadges}>{badges}</div>}
      </div>

      {isUnsentDraft(event) && (
        <div className={`${styles.band} ${styles.bandNotice}`}>
          <div className={styles.bandTitle}>Invite Not Sent</div>
          <div className={styles.bandText}>
            This invite hasn't been sent to attendees yet. Open it in Outlook to check the details and send it.
          </div>
          <div className={styles.bandActions}>
            <button
              type="button"
              className={styles.btn}
              onClick={() => {
                openInOutlook(event);
                onOpenedInOutlook();
              }}
            >
              Open in Outlook
            </button>
          </div>
        </div>
      )}

      {clashWith && (
        <div className={`${styles.band} ${styles.bandErr}`}>
          <div className={styles.bandTitle}>
            <WarnIcon />
            Clash
          </div>
          <div className={styles.bandText}>
            Overlaps <b>{displaySubject(clashWith)}</b> ({hm(minutesOfDay(clashWith.start))}–{hm(minutesOfDay(clashWith.end))}).
          </div>
          <div className={styles.bandActions}>
            <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={onSeeOptions}>
              See Options
            </button>
          </div>
        </div>
      )}

      {event.joinUrl && isActive(event) && (
        <button type="button" className={`${styles.btn} ${styles.btnCta}`} onClick={() => openJoinLink(event.joinUrl ?? "")}>
          <CameraIcon />
          Join Meeting
        </button>
      )}

      {!event.isOrganizer && !focus && !event.isCancelled && (
        <ReplySection event={event} writable={writable} onStage={onStage} />
      )}

      <MeetingActions
        event={event}
        writable={writable}
        moveMode={moveMode}
        canCancel={canCancel}
        onReschedule={onReschedule}
        onStage={onStage}
      />
      {!writable && event.isOrganizer && (
        <div className={styles.hint}>Change this meeting in Outlook — AiVA can't change your calendar with your current permissions.</div>
      )}

      {!focus && (
        <MeetingAttendees
          event={event}
          self={self}
          others={others}
          availability={availability}
          canEdit={canEdit}
          token={token}
          loadSchedules={loadSchedules}
          onStage={onStage}
        />
      )}

      {/* Keyed by meeting so each starts fresh on another meeting; the prefixes keep sibling keys unique. */}
      <MeetingAgenda
        key={`agenda-${event.id}`}
        event={event}
        invite={invite}
        inviteFailed={inviteFailed}
        canEdit={canEdit}
        userName={self.name}
        onStage={onStage}
      />

      <MeetingAttachments key={`attachments-${event.id}`} event={event} token={token} attachments={attachments} thread={thread} />

      {brief()}
    </>
  );
}
